/**
 * ContractSignPage
 * 전자계약 서명 페이지 (공개 URL)
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { SignaturePad } from '../components/SignaturePad';

interface Contract {
  contract_number: string;
  title: string;
  contract_type: string;
  signer_name: string;
  signer_phone: string;
  organization_name?: string;
  contract_data: {
    amount?: number;
    period?: string;
    plan_name?: string;
    terms?: string;
  };
  contract_html?: string;
  status: string;
  expires_at?: string;
}

type Step = 'loading' | 'view' | 'verify' | 'sign' | 'complete' | 'error';

export const ContractSignPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [contract, setContract] = useState<Contract | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 인증 상태
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeTimer, setCodeTimer] = useState(0);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // 서명 상태
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // 완료 상태
  const [contractNumber, setContractNumber] = useState('');

  useEffect(() => {
    fetchContract();
  }, [token]);

  // 타이머
  useEffect(() => {
    if (codeTimer > 0) {
      const timer = setTimeout(() => setCodeTimer(codeTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeTimer]);

  const fetchContract = async () => {
    try {
      const response = await fetch(`/api/contracts/sign/${token}`);

      if (response.status === 404) {
        setError('계약을 찾을 수 없습니다.');
        setStep('error');
        return;
      }
      if (response.status === 410) {
        setError('서명 요청이 만료되었거나 취소되었습니다.');
        setStep('error');
        return;
      }
      if (response.status === 409) {
        setError('이미 서명이 완료된 계약입니다.');
        setStep('error');
        return;
      }
      if (!response.ok) {
        throw new Error('계약 조회 실패');
      }

      const data = await response.json();
      setContract(data.contract);

      // 상태에 따라 단계 설정
      if (data.contract.status === 'verified') {
        setStep('sign');
      } else {
        setStep('view');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
      setStep('error');
    }
  };

  const handleSendCode = async () => {
    if (!phone.trim()) {
      alert('휴대폰 번호를 입력해주세요.');
      return;
    }

    try {
      setVerifyLoading(true);
      const response = await fetch(`/api/contracts/sign/${token}/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.replace(/-/g, '') }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || '인증 코드 발송 실패');
      }

      const data = await response.json();
      setCodeSent(true);
      setCodeTimer(data.valid_minutes * 60 || 180);
      alert('인증 코드가 발송되었습니다.');
    } catch (err) {
      alert(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      alert('인증 코드를 입력해주세요.');
      return;
    }

    try {
      setVerifyLoading(true);
      const response = await fetch(`/api/contracts/sign/${token}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone.replace(/-/g, ''),
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || '인증 실패');
      }

      setStep('sign');
    } catch (err) {
      alert(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSubmitSignature = async () => {
    if (!signatureData) {
      alert('서명을 입력해주세요.');
      return;
    }
    if (!agreed) {
      alert('계약 내용에 동의해주세요.');
      return;
    }

    try {
      setSubmitLoading(true);
      const response = await fetch(`/api/contracts/sign/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_image: signatureData }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || '서명 제출 실패');
      }

      const data = await response.json();
      setContractNumber(data.contract_number);
      setStep('complete');
    } catch (err) {
      alert(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setSubmitLoading(false);
    }
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return '-';
    return amount.toLocaleString() + '원';
  };

  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^0-9]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  // 에러 화면
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">😔</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">접근 불가</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // 로딩 화면
  if (step === 'loading' || !contract) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">계약서를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 완료 화면
  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">서명 완료</h1>
          <p className="text-gray-600 mb-4">
            계약서 서명이 완료되었습니다.
            <br />
            계약서 PDF가 등록된 연락처로 발송됩니다.
          </p>
          <div className="bg-gray-100 rounded-lg p-4 text-sm">
            <div className="text-gray-500">계약번호</div>
            <div className="font-mono font-bold">{contractNumber}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📝</span>
            <div>
              <h1 className="font-bold text-lg">전자계약 서명</h1>
              <p className="text-sm text-gray-500">{contract.contract_number}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* 진행 단계 */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {['계약 확인', '본인 인증', '서명'].map((label, idx) => {
            const stepNum = idx + 1;
            const currentStepNum = step === 'view' ? 1 : step === 'verify' ? 2 : 3;
            const isActive = stepNum === currentStepNum;
            const isComplete = stepNum < currentStepNum;

            return (
              <React.Fragment key={label}>
                {idx > 0 && (
                  <div
                    className={`h-0.5 w-12 ${
                      isComplete ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  />
                )}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : isComplete
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {isComplete ? '✓' : stepNum}
                  </div>
                  <span
                    className={`text-xs mt-1 ${
                      isActive ? 'text-indigo-600 font-semibold' : 'text-gray-500'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: 계약 확인 */}
        {step === 'view' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">{contract.title}</h2>

              {/* 계약 정보 */}
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <span className="text-gray-500">서명자:</span>{' '}
                  <span className="font-medium">{contract.signer_name}</span>
                </div>
                {contract.organization_name && (
                  <div>
                    <span className="text-gray-500">업체:</span>{' '}
                    <span className="font-medium">{contract.organization_name}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">플랜:</span>{' '}
                  <span className="font-medium">
                    {contract.contract_data?.plan_name || '-'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">월 이용료:</span>{' '}
                  <span className="font-medium text-indigo-600">
                    {formatAmount(contract.contract_data?.amount)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">계약 기간:</span>{' '}
                  <span className="font-medium">
                    {contract.contract_data?.period || '-'}
                  </span>
                </div>
              </div>

              {/* 계약 조건 */}
              {contract.contract_data?.terms && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">계약 조건</h3>
                  <pre className="p-4 bg-gray-50 rounded text-sm whitespace-pre-wrap">
                    {contract.contract_data.terms}
                  </pre>
                </div>
              )}

              {/* HTML 내용 */}
              {contract.contract_html && (
                <div
                  className="border-t pt-4 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: contract.contract_html }}
                />
              )}
            </div>

            <button
              onClick={() => setStep('verify')}
              className="w-full py-4 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
            >
              내용 확인 완료, 서명하기
            </button>
          </div>
        )}

        {/* Step 2: 본인 인증 */}
        {step === 'verify' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">본인 인증</h2>
            <p className="text-gray-600 mb-6">
              서명을 위해 등록된 휴대폰 번호로 본인 인증을 진행해주세요.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  휴대폰 번호
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                    placeholder="010-1234-5678"
                    className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    disabled={codeSent && codeTimer > 0}
                  />
                  <button
                    onClick={handleSendCode}
                    disabled={verifyLoading || (codeSent && codeTimer > 0)}
                    className="px-4 py-3 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 whitespace-nowrap"
                  >
                    {codeSent && codeTimer > 0
                      ? formatTimer(codeTimer)
                      : '인증번호 받기'}
                  </button>
                </div>
              </div>

              {codeSent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    인증 코드
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))
                    }
                    placeholder="6자리 숫자"
                    maxLength={6}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-center text-2xl tracking-widest"
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setStep('view')}
                className="flex-1 py-3 border rounded-lg hover:bg-gray-50"
              >
                이전
              </button>
              <button
                onClick={handleVerify}
                disabled={verifyLoading || !codeSent || verificationCode.length !== 6}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {verifyLoading ? '확인 중...' : '인증 확인'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: 서명 */}
        {step === 'sign' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">전자 서명</h2>
            <p className="text-gray-600 mb-6">
              아래 서명란에 서명을 해주세요.
            </p>

            <SignaturePad
              width={600}
              height={200}
              onSignatureChange={setSignatureData}
              className="mb-6"
            />

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  본인은 위 계약 내용을 충분히 확인하였으며, 이에 동의합니다.
                  본 전자계약서는 전자문서 및 전자거래 기본법에 따라 서면 계약과
                  동일한 법적 효력을 가집니다.
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('verify')}
                className="flex-1 py-3 border rounded-lg hover:bg-gray-50"
              >
                이전
              </button>
              <button
                onClick={handleSubmitSignature}
                disabled={submitLoading || !signatureData || !agreed}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitLoading ? '제출 중...' : '동의 및 서명 완료'}
              </button>
            </div>
          </div>
        )}
      </main>

      {/* 푸터 */}
      <footer className="bg-white border-t mt-8 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© 뉴어텐드 | www.newattend.com</p>
          <p className="mt-1">
            문의: 1234-5678 | support@newattend.com
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContractSignPage;
